import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

/**
 * KINOS business hours (Timor-Leste, Asia/Dili, UTC+9):
 * Mon-Fri 08:00-17:00. Sat/Sun closed.
 */
const OPEN_HOUR = 8;
const CLOSE_HOUR = 17;
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function tlNow() {
  // Dili is UTC+9 year-round (no DST)
  return new Date(Date.now() + 9 * 3600 * 1000);
}

function nextOpen(from) {
  const d = new Date(from);
  d.setUTCHours(OPEN_HOUR, 0, 0, 0);
  for (let i = 0; i < 8; i++) {
    const day = d.getUTCDay();
    const isWeekend = day === 0 || day === 6;
    if (!isWeekend && d.getTime() > from.getTime()) return d;
    d.setUTCDate(d.getUTCDate() + 1);
    d.setUTCHours(OPEN_HOUR, 0, 0, 0);
  }
  return d;
}

function computeState() {
  const now = tlNow();
  const day = now.getUTCDay();
  const hour = now.getUTCHours();
  const isWeekend = day === 0 || day === 6;
  const isOpen = !isWeekend && hour >= OPEN_HOUR && hour < CLOSE_HOUR;

  if (isOpen) {
    const closes = new Date(now);
    closes.setUTCHours(CLOSE_HOUR, 0, 0, 0);
    const mins = Math.round((closes - now) / 60000);
    return { open: true, label: mins > 60 ? `Open · closes ${CLOSE_HOUR}:00` : `Closing in ${mins}m` };
  }
  const opens = nextOpen(now);
  const sameDay = opens.getUTCDate() === now.getUTCDate() && opens.getUTCMonth() === now.getUTCMonth();
  const dayLabel = sameDay ? "today" : DAY_LABELS[opens.getUTCDay()];
  return { open: false, label: `Closed · opens ${dayLabel} ${OPEN_HOUR}:00` };
}

export default function BusinessHours({ compact = false }) {
  const [state, setState] = useState(() => computeState());
  useEffect(() => {
    const t = setInterval(() => setState(computeState()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  const cls = state.open
    ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/40"
    : "bg-slate-200 text-slate-600 border border-slate-200";

  return (
    <span
      data-testid="business-hours"
      className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-[10px] font-mono uppercase tracking-wider ${cls}`}
      title="Mon-Fri 08:00-17:00 (Asia/Dili · UTC+9)"
    >
      <span className={`h-1.5 w-1.5 rounded-full ${state.open ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />
      <Clock className="h-3 w-3" strokeWidth={1.5} />
      {compact ? (state.open ? "Open" : "Closed") : state.label}
    </span>
  );
}
